<template>
  <div class="ops-support-page" dir="rtl">
    <!-- Page Header -->
    <div class="d-flex flex-wrap align-center justify-space-between mb-5">
      <div>
        <div class="d-flex align-center">
          <div class="page-header-icon me-3">
            <v-icon color="#082C69" size="26">mdi-headset</v-icon>
          </div>
          <div>
            <h1 class="text-h5 font-weight-bold navy--text mb-1">الدعم والتذاكر — الكباتن</h1>
            <p class="text-body-2 text-secondary mb-0">
              إدارة شكاوى وبلاغات السائقين بنظام المستجيب الأول (First Responder Ownership) والمحادثة المباشرة
            </p>
          </div>
        </div>
      </div>
      <div class="d-flex align-center gap-2 mt-3 mt-sm-0">
        <v-chip small outlined color="#082C69" class="font-weight-bold me-2">
          <span class="live-pulse-dot me-2"></span>
          تحديث مباشر (2 ث)
        </v-chip>
        <v-btn
          small
          depressed
          color="#082C69"
          class="white--text font-weight-bold rounded-lg px-4"
          :loading="loading"
          @click="fetchTickets"
        >
          <v-icon left small>mdi-refresh</v-icon>
          تحديث القائمة
        </v-btn>
      </div>
    </div>

    <!-- KPI Cards Row -->
    <v-row dense class="mb-4">
      <v-col cols="6" sm="4" md="2">
        <v-card
          class="kpi-card pa-3 rounded-xl cursor-pointer"
          :class="{ 'kpi-active': filters.ownership === 'all' && filters.status === 'all' }"
           outlined
          @click="applyQuickKpi('all', 'all')"
        >
          <div class="d-flex align-center justify-space-between">
            <span class="text-caption font-weight-bold text-secondary">إجمالي التذاكر</span>
            <v-icon small color="#082C69">mdi-ticket-Confirmation-outline</v-icon>
          </div>
          <div class="text-h5 font-weight-bold navy--text mt-1">{{ kpis.total || 0 }}</div>
        </v-card>
      </v-col>

      <v-col cols="6" sm="4" md="2">
        <v-card
          class="kpi-card pa-3 rounded-xl cursor-pointer"
          :class="{ 'kpi-active-amber': filters.ownership === 'unassigned' }"
          outlined
          @click="applyQuickKpi('unassigned', 'all')"
        >
          <div class="d-flex align-center justify-space-between">
            <span class="text-caption font-weight-bold" style="color: #B45309;">غير مستلمة</span>
            <v-icon small color="#D97706">mdi-account-question-outline</v-icon>
          </div>
          <div class="text-h5 font-weight-bold mt-1" style="color: #B45309;">{{ kpis.unassigned || 0 }}</div>
        </v-card>
      </v-col>

      <v-col cols="6" sm="4" md="2">
        <v-card
          class="kpi-card pa-3 rounded-xl cursor-pointer"
          :class="{ 'kpi-active-green': filters.ownership === 'mine' }"
          outlined
          @click="applyQuickKpi('mine', 'all')"
        >
          <div class="d-flex align-center justify-space-between">
            <span class="text-caption font-weight-bold" style="color: #047857;">تذاكري المفتوحة</span>
            <v-icon small color="#059669">mdi-account-check-outline</v-icon>
          </div>
          <div class="text-h5 font-weight-bold mt-1" style="color: #047857;">{{ kpis.my_assigned || 0 }}</div>
        </v-card>
      </v-col>

      <v-col cols="6" sm="4" md="2">
        <v-card
          class="kpi-card pa-3 rounded-xl cursor-pointer"
          :class="{ 'kpi-active': filters.status === 'new' }"
          outlined
          @click="applyQuickKpi('all', 'new')"
        >
          <div class="d-flex align-center justify-space-between">
            <span class="text-caption font-weight-bold" style="color: #1D4ED8;">جديدة</span>
            <v-icon small color="#2563EB">mdi-fiber-new</v-icon>
          </div>
          <div class="text-h5 font-weight-bold mt-1" style="color: #1D4ED8;">{{ kpis.new || 0 }}</div>
        </v-card>
      </v-col>

      <v-col cols="6" sm="4" md="2">
        <v-card
          class="kpi-card pa-3 rounded-xl cursor-pointer"
          :class="{ 'kpi-active': filters.status === 'in_review' }"
          outlined
          @click="applyQuickKpi('all', 'in_review')"
        >
          <div class="d-flex align-center justify-space-between">
            <span class="text-caption font-weight-bold" style="color: #0369A1;">قيد المعالجة</span>
            <v-icon small color="#0284C7">mdi-progress-clock</v-icon>
          </div>
          <div class="text-h5 font-weight-bold mt-1" style="color: #0369A1;">{{ kpis.in_review || 0 }}</div>
        </v-card>
      </v-col>

      <v-col cols="6" sm="4" md="2">
        <v-card
          class="kpi-card pa-3 rounded-xl cursor-pointer"
          :class="{ 'kpi-active-red': filters.priority === 'urgent' }"
          outlined
          @click="applyUrgentFilter"
        >
          <div class="d-flex align-center justify-space-between">
            <span class="text-caption font-weight-bold" style="color: #B91C1C;">عاجلة / هامة</span>
            <v-icon small color="#DC2626">mdi-alert-decagram</v-icon>
          </div>
          <div class="text-h5 font-weight-bold mt-1" style="color: #B91C1C;">{{ kpis.urgent || 0 }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Ownership Tabs + Filter Bar -->
    <v-card outlined class="rounded-xl mb-4 pa-4 filter-card">
      <!-- Ownership Quick Scope Pills -->
      <div class="d-flex flex-wrap align-center justify-space-between mb-3 pb-3 border-bottom">
        <div class="d-flex flex-wrap align-center gap-2">
          <span class="text-caption font-weight-bold navy--text me-2">نطاق الملكية:</span>
          <v-btn
            small
            depressed
            class="rounded-lg font-weight-bold me-1 mb-1"
            :color="filters.ownership === 'all' ? '#082C69' : '#F1F5F9'"
            :class="filters.ownership === 'all' ? 'white--text' : 'navy--text'"
            @click="setOwnershipFilter('all')"
          >
            الكل ({{ kpis.total || 0 }})
          </v-btn>
          <v-btn
            small
            depressed
            class="rounded-lg font-weight-bold me-1 mb-1"
            :color="filters.ownership === 'unassigned' ? '#D97706' : '#FEF3C7'"
            :class="filters.ownership === 'unassigned' ? 'white--text' : 'amber-dark--text'"
            @click="setOwnershipFilter('unassigned')"
          >
            <v-icon left x-small>mdi-bell-alert-outline</v-icon>
            غير مستلمة ({{ kpis.unassigned || 0 }})
          </v-btn>
          <v-btn
            small
            depressed
            class="rounded-lg font-weight-bold me-1 mb-1"
            :color="filters.ownership === 'mine' ? '#059669' : '#D1FAE5'"
            :class="filters.ownership === 'mine' ? 'white--text' : 'green-dark--text'"
            @click="setOwnershipFilter('mine')"
          >
            <v-icon left x-small>mdi-account-check</v-icon>
            تذاكري ({{ kpis.my_assigned || 0 }})
          </v-btn>
          <v-btn
            small
            depressed
            class="rounded-lg font-weight-bold me-1 mb-1"
            :color="filters.ownership === 'others' ? '#475569' : '#F1F5F9'"
            :class="filters.ownership === 'others' ? 'white--text' : 'slate--text'"
            @click="setOwnershipFilter('others')"
          >
            <v-icon left x-small>mdi-lock-outline</v-icon>
            مستلمة بواسطة الآخرين ({{ kpis.others_assigned || 0 }})
          </v-btn>
        </div>

        <v-btn
          x-small
          text
          color="#64748B"
          class="font-weight-bold"
          @click="resetFilters"
        >
          <v-icon left x-small>mdi-filter-remove-outline</v-icon>
          إعادة تعيين الفلاتر
        </v-btn>
      </div>

      <!-- Detailed Filter Controls -->
      <v-row dense align="center">
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="filters.search"
            dense
            outlined
            hide-details
            clearable
            placeholder="بحث برقم التذكرة DRV، اسم السائق، الهاتف..."
            prepend-inner-icon="mdi-magnify"
            @keyup.enter="applyFilters"
            @click:clear=" () => { filters.search = ''; applyFilters(); }"
          ></v-text-field>
        </v-col>

        <v-col cols="6" sm="3" md="2">
          <v-select
            v-model="filters.status"
            :items="statusOptions"
            dense
            outlined
            hide-details
            label="الحالة"
            @change="applyFilters"
          ></v-select>
        </v-col>

        <v-col cols="6" sm="3" md="2">
          <v-select
            v-model="filters.ticket_type"
            :items="ticketTypeOptions"
            dense
            outlined
            hide-details
            label="النوع"
            @change="applyFilters"
          ></v-select>
        </v-col>

        <v-col cols="6" sm="3" md="2">
          <v-select
            v-model="filters.priority"
            :items="priorityOptions"
            dense
            outlined
            hide-details
            label="الأولوية"
            @change="applyFilters"
          ></v-select>
        </v-col>

        <v-col cols="6" sm="3" md="2">
          <v-select
            v-model="filters.assigned_to"
            :items="staffFilterOptions"
            dense
            outlined
            hide-details
            clearable
            label="الموظف المسؤول"
            @change="applyFilters"
          ></v-select>
        </v-col>

        <v-col cols="12" sm="12" md="1" class="d-flex justify-end">
          <v-btn
            block
            depressed
            color="#082C69"
            class="white--text font-weight-bold rounded-lg"
            height="40"
            @click="applyFilters"
          >
            بحث
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Tickets Table -->
    <v-card outlined class="rounded-xl mb-5 overflow-hidden table-card">
      <div class="px-4 py-3 d-flex align-center justify-space-between border-bottom bg-light-header">
        <div class="d-flex align-center">
          <v-icon small color="#082C69" class="me-2">mdi-format-list-bulleted-square</v-icon>
          <span class="text-subtitle-2 font-weight-bold navy--text">قائمة تذاكر وبلاغات السائقين</span>
          <v-chip x-small color="#E0E7FF" text-color="#082C69" class="font-weight-bold ms-2">
            {{ pagination.total || 0 }} تذكرة
          </v-chip>
        </div>
      </div>

      <v-simple-table class="ops-tickets-table">
        <thead>
          <tr>
            <th class="text-right">رقم التذكرة</th>
            <th class="text-right">السائق</th>
            <th class="text-right">النوع / الموضوع</th>
            <th class="text-right">الأولوية</th>
            <th class="text-right">الحالة</th>
            <th class="text-right">الموظف المسؤول</th>
            <th class="text-right">الرحلة المرتبطة</th>
            <th class="text-right">آخر تحديث</th>
            <th class="text-center">الإجراء</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading && !tickets.length">
            <td colspan="9" class="text-center py-8">
              <v-progress-circular indeterminate color="#082C69" size="28" class="me-2"></v-progress-circular>
              <span class="text-body-2 font-weight-bold navy--text">جاري تحميل تذاكر السائقين...</span>
            </td>
          </tr>
          <tr v-else-if="!tickets.length">
            <td colspan="9" class="text-center py-8">
              <v-icon size="40" color="#94A3B8" class="mb-2">mdi-ticket-outline</v-icon>
              <div class="text-subtitle-2 font-weight-bold navy--text">لا توجد تذاكر مطابقة للبحث</div>
              <div class="text-caption text-secondary">جرب تغيير فلاتر البحث أو نطاق الملكية</div>
            </td>
          </tr>
          <tr
            v-else
            v-for="item in tickets"
            :key="item.id"
            class="cursor-pointer ticket-row"
            :class="{
              'selected-ticket-row': selectedTicket && selectedTicket.id === item.id,
              'attention-ticket-row': hasAttention(item.id)
            }"
            @click="selectTicket(item)"
          >
            <!-- Ticket Code -->
            <td class="py-3">
              <div class="d-flex align-center">
                <span v-if="hasAttention(item.id)" class="attention-dot me-2" title="نشاط جديد"></span>
                <span class="mono-badge font-weight-bold">{{ item.ticket_code }}</span>
              </div>
            </td>

            <!-- Driver -->
            <td class="py-3">
              <div v-if="item.driver">
                <div class="font-weight-bold text-dark">{{ item.driver.name }}</div>
                <div class="text-caption text-secondary" dir="ltr">{{ item.driver.tel_number || '—' }}</div>
              </div>
              <span v-else class="text-caption text-secondary">كابتن #{{ item.driver_id }}</span>
            </td>

            <!-- Type / Subject -->
            <td class="py-3" style="max-width: 260px;">
              <div class="d-flex align-center mb-1">
                <span
                  class="type-pill me-1"
                  :class="item.ticket_type === 'report' ? 'type-report' : 'type-complaint'"
                >
                  {{ item.ticket_type_label }}
                </span>
                <span class="text-caption font-weight-bold text-secondary">{{ item.category_label }}</span>
              </div>
              <div class="text-body-2 font-weight-bold text-dark text-truncate" :title="item.subject">
                {{ item.subject || item.description }}
              </div>
            </td>

            <!-- Priority -->
            <td class="py-3">
              <span class="priority-pill" :class="'prio-' + (item.priority || 'normal')">
                {{ item.priority_label }}
              </span>
            </td>

            <!-- Status -->
            <td class="py-3">
              <span class="status-pill" :class="'st-' + item.status">
                {{ item.status_label }}
              </span>
            </td>

            <!-- Assigned Owner -->
            <td class="py-3">
              <div v-if="!item.assigned_to">
                <span class="owner-pill owner-unassigned">
                  <v-icon x-small color="#B45309" class="me-1">mdi-clock-outline</v-icon>
                  غير مستلمة
                </span>
              </div>
              <div v-else-if="item.ownership_state === 'mine'">
                <span class="owner-pill owner-mine">
                  <v-icon x-small color="#047857" class="me-1">mdi-account-check</v-icon>
                  {{ item.assigned_user ? item.assigned_user.name : 'أنت' }} (تذكرتك)
                </span>
              </div>
              <div v-else class="d-flex align-center flex-wrap gap-1">
                <span class="owner-pill owner-other">
                  <v-icon x-small color="#475569" class="me-1">mdi-account-lock-outline</v-icon>
                  {{ item.assigned_user ? item.assigned_user.name : ('موظف #' + item.assigned_to) }}
                </span>
                <span v-if="item.is_read_only_for_me" class="readonly-mini-badge ms-1">
                  للقراءة فقط
                </span>
              </div>
            </td>

            <!-- Planned Trip -->
            <td class="py-3">
              <div v-if="item.planned_trip">
                <span class="mono-code text-caption font-weight-bold navy--text">#{{ item.planned_trip.id }}</span>
                <div class="text-caption text-secondary text-truncate" style="max-width: 140px;">
                  {{ item.planned_trip.route_name || item.planned_trip.planned_date }}
                </div>
              </div>
              <span v-else class="text-caption text-secondary">—</span>
            </td>

            <!-- Updated At -->
            <td class="py-3">
              <div class="text-caption font-weight-medium text-dark" dir="ltr">
                {{ formatDateTime(item.updated_at || item.created_at) }}
              </div>
            </td>

            <!-- Action -->
            <td class="py-3 text-center" @click.stop>
              <v-btn
                x-small
                depressed
                :color="selectedTicket && selectedTicket.id === item.id ? '#F5A114' : '#082C69'"
                class="white--text font-weight-bold rounded-lg px-3"
                height="28"
                @click="selectTicket(item)"
              >
                <v-icon left x-small>{{ item.is_read_only_for_me ? 'mdi-eye-outline' : 'mdi-message-text-outline' }}</v-icon>
                {{ item.is_read_only_for_me ? 'عرض' : 'متابعة' }}
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>

      <!-- Pagination -->
      <div
        v-if="pagination.last_page > 1"
        class="px-4 py-3 d-flex align-center justify-space-between border-top bg-light-header"
      >
        <div class="text-caption font-weight-bold text-secondary">
          صفحة {{ pagination.current_page }} من {{ pagination.last_page }} (إجمالي {{ pagination.total }})
        </div>
        <v-pagination
          v-model="pagination.current_page"
          :length="pagination.last_page"
          :total-visible="6"
          color="#082C69"
          circle
          @input="onPageChange"
        ></v-pagination>
      </div>
    </v-card>

    <!-- SAME-PAGE TICKET WORKSPACE -->
    <div ref="workspaceAnchor"></div>
    <v-card
      v-if="selectedTicket"
      outlined
      class="rounded-xl workspace-card mb-8 overflow-hidden"
    >
      <!-- Workspace Top Header -->
      <div class="workspace-header px-5 py-4 d-flex flex-wrap align-center justify-space-between">
        <div class="d-flex align-center flex-wrap gap-2">
          <span class="mono-badge-lg font-weight-bold me-2">{{ selectedTicket.ticket_code }}</span>
          <span
            class="type-pill me-2"
            :class="selectedTicket.ticket_type === 'report' ? 'type-report' : 'type-complaint'"
          >
            {{ selectedTicket.ticket_type_label }}
          </span>
          <span class="status-pill me-2" :class="'st-' + selectedTicket.status">
            {{ selectedTicket.status_label }}
          </span>
          <span class="priority-pill me-2" :class="'prio-' + (selectedTicket.priority || 'normal')">
            {{ selectedTicket.priority_label }}
          </span>
          <h2 class="text-subtitle-1 font-weight-bold navy--text mb-0 ms-1">
            {{ selectedTicket.subject || selectedTicket.category_label }}
          </h2>
        </div>

        <div class="d-flex align-center gap-2 mt-2 mt-md-0">
          <!-- SuperAdmin Ownership Override Buttons -->
          <template v-if="isSuperAdmin">
            <v-btn
              x-small
              outlined
              color="#082C69"
              class="font-weight-bold rounded-lg me-1"
              height="30"
              @click="openReassignDialog"
            >
              <v-icon left x-small>mdi-account-switch-outline</v-icon>
              إعادة توجيه التذكرة
            </v-btn>
            <v-btn
              v-if="selectedTicket.assigned_to"
              x-small
              outlined
              color="warning"
              class="font-weight-bold rounded-lg me-1"
              height="30"
              @click="openReleaseDialog"
            >
              <v-icon left x-small>mdi-account-off-outline</v-icon>
              إلغاء التخصيص
            </v-btn>
          </template>

          <v-btn
            icon
            small
            title="تحديث بيانات التذكرة"
            :loading="loadingDetail"
            @click="refreshSelectedTicket(false)"
          >
            <v-icon small color="#082C69">mdi-refresh</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            title="إغلاق مساحة العمل"
            @click="closeWorkspace"
          >
            <v-icon small color="#64748B">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Ownership Banner -->
      <!-- Case 1: Read-Only for another Role 4 user -->
      <div
        v-if="selectedTicket.is_read_only_for_me"
        class="ownership-banner banner-readonly px-5 py-3 d-flex align-center justify-space-between"
      >
        <div class="d-flex align-center">
          <v-icon color="#B91C1C" class="me-2">mdi-lock-alert-outline</v-icon>
          <div>
            <div class="text-body-2 font-weight-bold" style="color: #991B1B;">
              هذه التذكرة مستلمة بواسطة ({{ selectedTicket.assigned_user ? selectedTicket.assigned_user.name : ('موظف #' + selectedTicket.assigned_to) }}) — للعرض فقط
            </div>
            <div class="text-caption" style="color: #B91C1C;">
              وفقاً لنظام المستجيب الأول، يمكنك الاطلاع على المحادثة والسجل ولكن التعديل والرد متاح فقط للموظف المسؤول عن التذكرة.
            </div>
          </div>
        </div>
        <span class="readonly-badge">للقراءة فقط (Read-Only)</span>
      </div>

      <!-- Case 2: Unassigned ticket -->
      <div
        v-else-if="!selectedTicket.assigned_to"
        class="ownership-banner banner-unassigned px-5 py-3 d-flex align-center justify-space-between"
      >
        <div class="d-flex align-center">
          <v-icon color="#B45309" class="me-2">mdi-hand-Pointing-left</v-icon>
          <div>
            <div class="text-body-2 font-weight-bold" style="color: #92400E;">
              هذه التذكرة غير مستلمة حالياً — متاحة للاستلام
            </div>
            <div class="text-caption" style="color: #B45309;">
              بمجرد إرسالك لأول رد عام للسائق، سيتم تسجيل التذكرة باسمك تلقائياً كمسؤول عنها (First Responder).
            </div>
          </div>
        </div>
        <span class="unassigned-badge">غير مستلمة</span>
      </div>

      <!-- Case 3: Owned by current user (or viewed by SuperAdmin) -->
      <div
        v-else
        class="ownership-banner banner-owned px-5 py-2 d-flex align-center justify-space-between"
      >
        <div class="d-flex align-center">
          <v-icon small color="#047857" class="me-2">mdi-shield-check-outline</v-icon>
          <span class="text-caption font-weight-bold" style="color: #065F46;">
            الموظف المسؤول عن التذكرة:
            <strong>{{ selectedTicket.assigned_user ? selectedTicket.assigned_user.name : ('#' + selectedTicket.assigned_to) }}</strong>
            <span v-if="selectedTicket.assigned_at" class="ms-2" dir="ltr">({{ formatDateTime(selectedTicket.assigned_at) }})</span>
          </span>
        </div>
        <span class="owned-badge">
          {{ selectedTicket.ownership_state === 'mine' ? 'مسجلة باسمك' : 'صلاحية كاملة' }}
        </span>
      </div>

      <!-- Workspace Body: 2 Columns (Conversation & Composer on Right, Context & History on Left) -->
      <v-row no-gutters>
        <!-- Main Column: Conversation Thread + Reply Composer -->
        <v-col cols="12" md="8" class="border-left-md">
          <!-- Initial Ticket Description Box -->
          <div class="pa-4 bg-ticket-summary border-bottom">
            <div class="d-flex align-center justify-space-between mb-1">
              <span class="text-caption font-weight-bold navy--text">
                <v-icon x-small color="#082C69" class="me-1">mdi-text-box-outline</v-icon>
                نص البلاغ / الشكوى الأساسية من السائق:
              </span>
              <span class="text-caption text-secondary" dir="ltr">{{ formatDateTime(selectedTicket.created_at) }}</span>
            </div>
            <div class="text-body-2 text-dark font-weight-medium whitespace-pre-wrap">
              {{ selectedTicket.description }}
            </div>
          </div>

          <!-- Live Conversation Messages -->
          <div class="px-4 py-3 d-flex align-center justify-space-between border-bottom bg-white">
            <div class="d-flex align-center">
              <v-icon small color="#082C69" class="me-2">mdi-forum-outline</v-icon>
              <span class="text-subtitle-2 font-weight-bold navy--text">سجل المحادثة المباشرة</span>
              <v-chip x-small color="#F1F5F9" text-color="#082C69" class="font-weight-bold ms-2">
                {{ (selectedTicket.messages || []).length }} رسالة
              </v-chip>
            </div>
            <span class="text-caption text-secondary">
              <span class="live-pulse-dot me-1"></span>
              متصل مباشرة
            </span>
          </div>

          <div ref="chatScrollBox" class="chat-thread-container pa-4">
            <div v-if="!selectedTicket.messages || !selectedTicket.messages.length" class="text-center py-8">
              <v-icon size="36" color="#94A3B8">mdi-message-off-outline</v-icon>
              <div class="text-caption text-secondary mt-2">لا توجد رسائل مسجلة بعد</div>
            </div>

            <div
              v-for="msg in selectedTicket.messages"
              :key="'msg-' + msg.id"
              class="chat-bubble-wrapper mb-4"
              :class="{
                'wrapper-driver': msg.sender_type === 'driver',
                'wrapper-internal': msg.is_internal,
                'wrapper-staff': msg.sender_type !== 'driver' && !msg.is_internal
              }"
            >
              <div
                class="chat-bubble pa-3 rounded-xl"
                :class="{
                  'bubble-driver': msg.sender_type === 'driver',
                  'bubble-internal': msg.is_internal,
                  'bubble-staff': msg.sender_type !== 'driver' && !msg.is_internal
                }"
              >
                <!-- Bubble Header -->
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="d-flex align-center">
                    <v-icon
                      x-small
                      class="me-1"
                      :color="msg.is_internal ? '#B45309' : (msg.sender_type === 'driver' ? '#082C69' : '#047857')"
                    >
                      {{ msg.is_internal ? 'mdi-lock-outline' : (msg.sender_type === 'driver' ? 'mdi-steering' : 'mdi-headset') }}
                    </v-icon>
                    <span class="text-caption font-weight-bold text-dark">
                      {{ msg.sender_name }}
                    </span>
                    <span v-if="msg.sender_type === 'driver'" class="role-mini-tag tag-driver ms-2">السائق</span>
                    <span v-else-if="msg.is_internal" class="role-mini-tag tag-internal ms-2">
                      ملاحظة داخلية (لا تظهر للسائق)
                    </span>
                    <span v-else class="role-mini-tag tag-ops ms-2">فريق التشغيل</span>
                  </div>
                  <span class="text-caption text-secondary ms-3" dir="ltr">
                    {{ formatDateTime(msg.created_at) }}
                  </span>
                </div>

                <!-- Bubble Message Text -->
                <div class="text-body-2 text-dark whitespace-pre-wrap mt-1">
                  {{ msg.message }}
                </div>

                <!-- Message Attachments -->
                <div v-if="msg.attachments && msg.attachments.length" class="mt-2 pt-2 border-top d-flex flex-wrap gap-2">
                  <v-btn
                    v-for="att in msg.attachments"
                    :key="'att-' + att.id"
                    x-small
                    outlined
                    color="#082C69"
                    class="rounded-lg text-none"
                    @click="downloadAttachment(att)"
                  >
                    <v-icon left x-small>mdi-paperclip</v-icon>
                    {{ att.file_name }}
                  </v-btn>
                </div>
              </div>
            </div>
          </div>

          <!-- Composer Section -->
          <div class="composer-container pa-4 border-top bg-white">
            <!-- Read-only notice inside composer if locked -->
            <div v-if="selectedTicket.is_read_only_for_me" class="text-center py-3 px-4 rounded-lg bg-readonly-soft">
              <v-icon small color="#B91C1C" class="me-1">mdi-lock</v-icon>
              <span class="text-body-2 font-weight-bold" style="color: #991B1B;">
                الرد والتعديل غير متاح لأن التذكرة مستلمة بواسطة {{ selectedTicket.assigned_user ? selectedTicket.assigned_user.name : 'موظف آخر' }}.
              </span>
            </div>

            <template v-else>
              <!-- Composer Mode Tabs: Public Reply vs Internal Note -->
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="d-flex align-center gap-2">
                  <v-btn
                    small
                    depressed
                    class="rounded-lg font-weight-bold me-2"
                    :color="!composer.is_internal ? '#082C69' : '#F1F5F9'"
                    :class="!composer.is_internal ? 'white--text' : 'navy--text'"
                    @click="composer.is_internal = false"
                  >
                    <v-icon left x-small>mdi-reply</v-icon>
                    رد عام للسائق
                  </v-btn>
                  <v-btn
                    small
                    depressed
                    class="rounded-lg font-weight-bold"
                    :color="composer.is_internal ? '#D97706' : '#FEF3C7'"
                    :class="composer.is_internal ? 'white--text' : 'amber-dark--text'"
                    @click="composer.is_internal = true"
                  >
                    <v-icon left x-small>mdi-note-lock-outline</v-icon>
                    ملاحظة داخلية (للتشغيل فقط)
                  </v-btn>
                </div>

                <!-- Optional Status Transition on Public Reply -->
                <div v-if="!composer.is_internal" style="width: 190px;">
                  <v-select
                    v-model="composer.status"
                    :items="replyStatusOptions"
                    dense
                    outlined
                    hide-details
                    label="حالة التذكرة بعد الرد"
                  ></v-select>
                </div>
              </div>

              <!-- Conflict / Error Alert -->
              <v-alert
                v-if="composerError"
                type="error"
                dense
                outlined
                dismissible
                class="mb-3 text-body-2 font-weight-bold"
                @input="composerError = null"
              >
                {{ composerError }}
              </v-alert>

              <v-textarea
                v-model="composer.message"
                outlined
                rows="3"
                hide-details
                :placeholder="composer.is_internal
                  ? 'اكتب ملاحظة داخلية لفريق التشغيل فقط (لن يراها السائق ولن ترسل إشعاراً له)...'
                  : (!selectedTicket.assigned_to
                      ? 'اكتب ردك للسائق... (إرسال هذا الرد سيقوم بتسجيل التذكرة باسمك تلقائياً)'
                      : 'اكتب ردك للسائق هنا...')"
                :background-color="composer.is_internal ? '#FFFBEB' : '#FFFFFF'"
              ></v-textarea>

              <div class="d-flex flex-wrap align-center justify-space-between mt-3">
                <!-- Attachment file input -->
                <div class="d-flex align-center flex-wrap gap-2">
                  <input
                    ref="replyFileInput"
                    type="file"
                    multiple
                    class="d-none"
                    @change="onReplyFilesSelected"
                  />
                  <v-btn
                    small
                    outlined
                    color="#082C69"
                    class="rounded-lg font-weight-bold"
                    @click="$refs.replyFileInput && $refs.replyFileInput.click()"
                  >
                    <v-icon left small>mdi-paperclip</v-icon>
                    إرفاق ملفات
                  </v-btn>
                  <v-chip
                    v-for="(f, idx) in composer.files"
                    :key="'file-' + idx"
                    small
                    close
                    color="#E0E7FF"
                    text-color="#082C69"
                    class="ms-1"
                    @click:close="removeReplyFile(idx)"
                  >
                    {{ f.name }}
                  </v-chip>
                </div>

                <v-btn
                  depressed
                  :color="composer.is_internal ? '#D97706' : '#082C69'"
                  class="white--text font-weight-bold rounded-lg px-6 mt-2 mt-sm-0"
                  :loading="submittingReply"
                  :disabled="!composer.message || !composer.message.trim()"
                  @click="submitReply"
                >
                  <v-icon left small>{{ composer.is_internal ? 'mdi-lock-check' : 'mdi-send' }}</v-icon>
                  {{ composer.is_internal
                    ? 'حفظ الملاحظة الداخلية'
                    : (!selectedTicket.assigned_to ? 'استلام التذكرة وإرسال الرد' : 'إرسال الرد للسائق') }}
                </v-btn>
              </div>
            </template>
          </div>
        </v-col>

        <!-- Side Column: Driver Info, Status/Priority Controls, Attachments, History -->
        <v-col cols="12" md="4" class="bg-side-panel pa-4">
          <!-- 1. Quick Status & Priority Controls -->
          <div class="side-section-card pa-3 rounded-xl bg-white mb-4 border">
            <div class="text-subtitle-2 font-weight-bold navy--text mb-3 d-flex align-center">
              <v-icon small color="#082C69" class="me-1">mdi-tune-variant</v-icon>
              إدارة الحالة والأولوية
            </div>

            <v-row dense>
              <v-col cols="6">
                <v-select
                  :value="selectedTicket.status"
                  :items="allStatusEditOptions"
                  dense
                  outlined
                  hide-details
                  label="الحالة"
                  :disabled="!selectedTicket.can_write || updatingStatus"
                  @change="changeTicketStatus"
                ></v-select>
              </v-col>
              <v-col cols="6">
                <v-select
                  :value="selectedTicket.priority || 'normal'"
                  :items="priorityEditOptions"
                  dense
                  outlined
                  hide-details
                  label="الأولوية"
                  :disabled="!selectedTicket.can_write || updatingPriority"
                  @change="changeTicketPriority"
                ></v-select>
              </v-col>
            </v-row>

            <div class="d-flex flex-wrap gap-2 mt-3" v-if="selectedTicket.can_write">
              <v-btn
                v-if="selectedTicket.status !== 'waiting_driver' && selectedTicket.status !== 'closed'"
                x-small
                outlined
                color="#B45309"
                class="font-weight-bold rounded-lg flex-grow-1"
                height="30"
                @click="changeTicketStatus('waiting_driver')"
              >
                بانتظار السائق
              </v-btn>
              <v-btn
                v-if="selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed'"
                x-small
                outlined
                color="#047857"
                class="font-weight-bold rounded-lg flex-grow-1"
                height="30"
                @click="changeTicketStatus('resolved')"
              >
                تم الحل
              </v-btn>
              <v-btn
                v-if="selectedTicket.status !== 'closed'"
                x-small
                depressed
                color="#DC2626"
                class="white--text font-weight-bold rounded-lg flex-grow-1"
                height="30"
                @click="changeTicketStatus('closed')"
              >
                إغلاق التذكرة
              </v-btn>
              <v-btn
                v-else
                x-small
                outlined
                color="#082C69"
                class="font-weight-bold rounded-lg flex-grow-1"
                height="30"
                @click="changeTicketStatus('in_review')"
              >
                إعادة فتح التذكرة
              </v-btn>
            </div>
          </div>

          <!-- 2. Driver Information Card -->
          <div class="side-section-card pa-3 rounded-xl bg-white mb-4 border">
            <div class="text-subtitle-2 font-weight-bold navy--text mb-2 d-flex align-center justify-space-between">
              <span>
                <v-icon small color="#082C69" class="me-1">mdi-card-account-details-outline</v-icon>
                بيانات السائق (الكابتن)
              </span>
              <router-link
                v-if="selectedTicket.driver_id"
                :to="{ path: '/operations/drivers', query: { driver_id: selectedTicket.driver_id } }"
                class="text-caption font-weight-bold text-decoration-none"
                style="color: #0284C7;"
              >
                ملف السائق
              </router-link>
            </div>

            <div v-if="selectedTicket.driver" class="text-body-2">
              <div class="d-flex justify-space-between py-1 border-bottom-dashed">
                <span class="text-secondary">الاسم:</span>
                <span class="font-weight-bold text-dark">{{ selectedTicket.driver.name }}</span>
              </div>
              <div class="d-flex justify-space-between py-1 border-bottom-dashed">
                <span class="text-secondary">الهاتف:</span>
                <span class="font-weight-bold text-dark" dir="ltr">{{ selectedTicket.driver.tel_number || '—' }}</span>
              </div>
              <div class="d-flex justify-space-between py-1">
                <span class="text-secondary">البريد:</span>
                <span class="text-caption font-weight-medium text-dark" dir="ltr">{{ selectedTicket.driver.email || '—' }}</span>
              </div>
            </div>
          </div>

          <!-- 3. Operational Trip Context -->
          <div class="side-section-card pa-3 rounded-xl bg-white mb-4 border">
            <div class="text-subtitle-2 font-weight-bold navy--text mb-2 d-flex align-center">
              <v-icon small color="#082C69" class="me-1">mdi-bus-clock</v-icon>
              السياق التشغيلي والرحلات
            </div>

            <div v-if="selectedTicket.planned_trip" class="pa-2 rounded-lg bg-blue-soft mb-2">
              <div class="text-caption font-weight-bold navy--text">
                الرحلة المرتبطة مباشرة: #{{ selectedTicket.planned_trip.id }}
              </div>
              <div class="text-caption text-dark">
                المسار: {{ selectedTicket.planned_trip.route_name || '—' }}
                <span v-if="selectedTicket.planned_trip.bus_plate"> | الباص: {{ selectedTicket.planned_trip.bus_plate }}</span>
              </div>
              <div class="text-caption text-secondary" dir="ltr">
                التاريخ: {{ selectedTicket.planned_trip.planned_date || '—' }}
              </div>
            </div>

            <div v-if="selectedTicket.driver_recent_trips && selectedTicket.driver_recent_trips.length">
              <div class="text-caption font-weight-bold text-secondary mb-1">أحدث رحلات الكابتن:</div>
              <div
                v-for="rt in selectedTicket.driver_recent_trips"
                :key="'rt-' + rt.id"
                class="d-flex align-center justify-space-between py-1 border-bottom-dashed text-caption"
              >
                <div>
                  <span class="mono-code font-weight-bold navy--text">#{{ rt.id }}</span>
                  <span class="ms-1 text-dark">{{ rt.route_name || rt.route_code || 'رحلة' }}</span>
                </div>
                <span class="text-secondary" dir="ltr">{{ rt.planned_date }}</span>
              </div>
            </div>
            <div v-else-if="!selectedTicket.planned_trip" class="text-caption text-secondary">
              لا توجد رحلات مرتبطة مسجلة حالياً
            </div>
          </div>

          <!-- 4. Audit & Ownership Timeline -->
          <div class="side-section-card pa-3 rounded-xl bg-white border">
            <div class="text-subtitle-2 font-weight-bold navy--text mb-2 d-flex align-center">
              <v-icon small color="#082C69" class="me-1">mdi-history</v-icon>
              سجل الحركات والملكية ({{ (selectedTicket.history || []).length }})
            </div>

            <div class="history-timeline-list" v-if="selectedTicket.history && selectedTicket.history.length">
              <div
                v-for="h in selectedTicket.history"
                :key="'hist-' + h.id"
                class="history-item py-2 border-bottom-dashed"
              >
                <div class="d-flex align-center justify-space-between">
                  <span class="text-caption font-weight-bold navy--text">
                    {{ formatHistoryActionLabel(h.action_type) }}
                  </span>
                  <span class="text-caption text-secondary" style="font-size: 10px !important;" dir="ltr">
                    {{ formatDateTime(h.created_at) }}
                  </span>
                </div>
                <div class="text-caption text-dark mt-1">
                  {{ h.description }}
                </div>
                <div v-if="h.reason" class="text-caption font-weight-bold mt-1" style="color: #B45309;">
                  السبب: {{ h.reason }}
                </div>
              </div>
            </div>
            <div v-else class="text-caption text-secondary">
              لا توجد حركات مسجلة بعد
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- SuperAdmin Reassign Modal -->
    <v-dialog v-model="reassignDialog.open" max-width="480">
      <v-card class="rounded-xl pa-4" dir="rtl">
        <div class="text-subtitle-1 font-weight-bold navy--text mb-3">
          إعادة توجيه التذكرة ({{ selectedTicket ? selectedTicket.ticket_code : '' }})
        </div>
        <v-select
          v-model="reassignDialog.assigned_to"
          :items="staffSelectItems"
          outlined
          dense
          label="اختر موظف التشغيل الجديد (Role 4)"
          class="mb-3"
        ></v-select>
        <v-textarea
          v-model="reassignDialog.reason"
          outlined
          rows="3"
          label="سبب إعادة التوجيه (إلزامي)"
          placeholder="مثال: انتهاء وردية الموظف السابق..."
        ></v-textarea>
        <div class="d-flex justify-end gap-2 mt-2">
          <v-btn text small @click="reassignDialog.open = false">إلغاء</v-btn>
          <v-btn
            depressed
            small
            color="#082C69"
            class="white--text font-weight-bold px-4"
            :loading="reassignDialog.loading"
            :disabled="!reassignDialog.assigned_to || !reassignDialog.reason.trim()"
            @click="confirmReassign"
          >
            تأكيد التوجيه
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- SuperAdmin Release Ownership Modal -->
    <v-dialog v-model="releaseDialog.open" max-width="460">
      <v-card class="rounded-xl pa-4" dir="rtl">
        <div class="text-subtitle-1 font-weight-bold navy--text mb-2">
          إلغاء تخصيص التذكرة ({{ selectedTicket ? selectedTicket.ticket_code : '' }})
        </div>
        <p class="text-caption text-secondary mb-3">
          سيتم إعادة التذكرة إلى حالة "غير مستلمة" لتصبح متاحة لأول موظف تشغيل يرد عليها.
        </p>
        <v-textarea
          v-model="releaseDialog.reason"
          outlined
          rows="3"
          label="سبب إلغاء التخصيص (إلزامي)"
        ></v-textarea>
        <div class="d-flex justify-end gap-2 mt-2">
          <v-btn text small @click="releaseDialog.open = false">إلغاء</v-btn>
          <v-btn
            depressed
            small
            color="warning"
            class="white--text font-weight-bold px-4"
            :loading="releaseDialog.loading"
            :disabled="!releaseDialog.reason.trim()"
            @click="confirmRelease"
          >
            إلغاء التخصيص
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Feedback Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="3500"
      top
      left
    >
      <span class="font-weight-bold">{{ snackbar.text }}</span>
    </v-snackbar>
  </div>
</template>

<script>
import { getUserRole, getCurrentUser } from '@/services/AuthService'

export default {
  name: 'OperationsDriverSupportIndex',
  data() {
    return {
      loading: false,
      loadingDetail: false,
      submittingReply: false,
      updatingStatus: false,
      updatingPriority: false,
      tickets: [],
      staffList: [],
      kpis: {
        total: 0,
        new: 0,
        in_review: 0,
        waiting_driver: 0,
        replied: 0,
        resolved: 0,
        closed: 0,
        open: 0,
        urgent: 0,
        unassigned: 0,
        my_assigned: 0,
        others_assigned: 0,
      },
      pagination: {
        total: 0,
        per_page: 15,
        current_page: 1,
        last_page: 1,
      },
      filters: {
        ownership: 'all',
        assigned_to: null,
        status: 'all',
        ticket_type: 'all',
        priority: 'all',
        search: '',
      },
      selectedTicket: null,
      lastPolledMessageId: 0,
      lastPolledHistoryId: 0,
      lastPolledAssignedTo: null,
      lastPolledStatus: null,
      livePollTimer: null,
      isPollingActivity: false,
      attentionTicketIds: [],
      composer: {
        message: '',
        is_internal: false,
        status: 'replied',
        files: [],
      },
      composerError: null,
      reassignDialog: {
        open: false,
        assigned_to: null,
        reason: '',
        loading: false,
      },
      releaseDialog: {
        open: false,
        reason: '',
        loading: false,
      },
      snackbar: {
        show: false,
        text: '',
        color: '#082C69',
      },
      statusOptions: [
        { text: 'كل الحالات', value: 'all' },
        { text: 'مفتوحة (غير مغلقة)', value: 'open' },
        { text: 'جديدة', value: 'new' },
        { text: 'قيد المعالجة', value: 'in_review' },
        { text: 'بانتظار السائق', value: 'waiting_driver' },
        { text: 'تم الرد', value: 'replied' },
        { text: 'تم الحل', value: 'resolved' },
        { text: 'مغلقة', value: 'closed' },
      ],
      allStatusEditOptions: [
        { text: 'جديدة', value: 'new' },
        { text: 'قيد المعالجة', value: 'in_review' },
        { text: 'بانتظار السائق', value: 'waiting_driver' },
        { text: 'تم الرد', value: 'replied' },
        { text: 'تم الحل', value: 'resolved' },
        { text: 'مغلقة', value: 'closed' },
      ],
      replyStatusOptions: [
        { text: 'تم الرد (افتراضي)', value: 'replied' },
        { text: 'قيد المعالجة', value: 'in_review' },
        { text: 'بانتظار السائق', value: 'waiting_driver' },
        { text: 'تم الحل', value: 'resolved' },
      ],
      ticketTypeOptions: [
        { text: 'كل الأنواع', value: 'all' },
        { text: 'شكوى سائق', value: 'complaint' },
        { text: 'بلاغ تشغيلي', value: 'report' },
      ],
      priorityOptions: [
        { text: 'كل الأولويات', value: 'all' },
        { text: 'منخفضة', value: 'low' },
        { text: 'عادية', value: 'normal' },
        { text: 'متوسطة', value: 'medium' },
        { text: 'مرتفعة', value: 'high' },
        { text: 'عاجلة', value: 'urgent' },
      ],
      priorityEditOptions: [
        { text: 'منخفضة', value: 'low' },
        { text: 'عادية', value: 'normal' },
        { text: 'متوسطة', value: 'medium' },
        { text: 'مرتفعة', value: 'high' },
        { text: 'عاجلة', value: 'urgent' },
      ],
    }
  },
  computed: {
    isSuperAdmin() {
      const r = Number(getUserRole())
      if (r === 0) return true
      try {
        const u = getCurrentUser()
        if (u && Number(u.role) === 0) return true
      } catch (e) {}
      return false
    },
    staffFilterOptions() {
      const base = [
        { text: 'الكل', value: null },
        { text: 'غير مستلمة', value: 'unassigned' },
      ]
      this.staffList.forEach(s => {
        base.push({ text: s.name, value: s.id })
      })
      return base
    },
    staffSelectItems() {
      return this.staffList.map(s => ({
        text: `${s.name} (${s.email || s.tel_number || '#' + s.id})`,
        value: s.id,
      }))
    },
  },
  watch: {
    '$route.query': {
      handler(newQuery) {
        if (newQuery && newQuery.id) {
          const targetId = parseInt(newQuery.id, 10)
          if (targetId && (!this.selectedTicket || this.selectedTicket.id !== targetId)) {
            this.openTicketById(targetId)
          }
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchStaffList()
    this.fetchTickets().then(() => {
      if (this.$route.query && this.$route.query.id) {
        const targetId = parseInt(this.$route.query.id, 10)
        if (targetId) {
          this.openTicketById(targetId)
        }
      }
    })
    this.startLivePolling()
    window.addEventListener('beepgo:ops-activity', this.onGlobalOpsActivity)
  },
  beforeDestroy() {
    this.stopLivePolling()
    window.removeEventListener('beepgo:ops-activity', this.onGlobalOpsActivity)
  },
  methods: {
    showToast(text, color = '#082C69') {
      this.snackbar.text = text
      this.snackbar.color = color
      this.snackbar.show = true
    },
    fetchStaffList() {
      axios.get('/operations/support-tickets/staff').then(res => {
        if (res && res.data && res.data.data) {
          this.staffList = res.data.data
        }
      }).catch(() => {})
    },
    fetchTickets() {
      this.loading = true
      const params = {
        page: this.pagination.current_page,
        per_page: this.pagination.per_page,
        ownership: this.filters.ownership,
        assigned_to: this.filters.assigned_to,
        status: this.filters.status,
        ticket_type: this.filters.ticket_type,
        priority: this.filters.priority,
        search: this.filters.search,
      }
      return axios.get('/operations/support-tickets', { params }).then(res => {
        if (res && res.data) {
          this.tickets = res.data.data || []
          if (res.data.pagination) {
            this.pagination = res.data.pagination
          }
          if (res.data.kpis) {
            this.kpis = res.data.kpis
          }
        }
      }).catch(err => {
        console.error('Error loading driver support tickets:', err)
      }).finally(() => {
        this.loading = false
      })
    },
    applyFilters() {
      this.pagination.current_page = 1
      this.fetchTickets()
    },
    setOwnershipFilter(scope) {
      this.filters.ownership = scope
      this.filters.assigned_to = null
      this.applyFilters()
    },
    applyQuickKpi(ownership, status) {
      this.filters.ownership = ownership
      this.filters.status = status
      this.filters.priority = 'all'
      this.applyFilters()
    },
    applyUrgentFilter() {
      this.filters.ownership = 'all'
      this.filters.status = 'open'
      this.filters.priority = 'urgent'
      this.applyFilters()
    },
    resetFilters() {
      this.filters = {
        ownership: 'all',
        assigned_to: null,
        status: 'all',
        ticket_type: 'all',
        priority: 'all',
        search: '',
      }
      this.applyFilters()
    },
    onPageChange(page) {
      this.pagination.current_page = page
      this.fetchTickets()
    },
    hasAttention(ticketId) {
      return this.attentionTicketIds.includes(ticketId)
    },
    clearAttention(ticketId) {
      this.attentionTicketIds = this.attentionTicketIds.filter(id => id !== ticketId)
    },
    selectTicket(row) {
      if (!row || !row.id) return
      this.clearAttention(row.id)
      this.openTicketById(row.id, true)
    },
    openTicketById(ticketId, scroll = true) {
      this.loadingDetail = true
      this.composerError = null
      axios.get(`/operations/support-tickets/${ticketId}`).then(res => {
        if (res && res.data && res.data.data) {
          this.applyTicketDetailData(res.data.data, true)
          if (scroll && this.$refs.workspaceAnchor) {
            this.$nextTick(() => {
              this.$refs.workspaceAnchor.scrollIntoView({ behavior: 'smooth', block: 'start' })
            })
          }
        }
      }).catch(err => {
        console.error('Failed to load ticket detail:', err)
        this.showToast('تعذر تحميل تفاصيل التذكرة', 'error')
      }).finally(() => {
        this.loadingDetail = false
      })
    },
    applyTicketDetailData(detail, scrollChat = false) {
      this.selectedTicket = detail
      const msgs = detail.messages || []
      this.lastPolledMessageId = msgs.length ? Math.max(...msgs.map(m => m.id)) : 0
      const hist = detail.history || []
      this.lastPolledHistoryId = hist.length ? Math.max(...hist.map(h => h.id)) : 0
      this.lastPolledAssignedTo = detail.assigned_to || null
      this.lastPolledStatus = detail.status || null

      // Sync row in table
      const idx = this.tickets.findIndex(t => t.id === detail.id)
      if (idx !== -1) {
        this.$set(this.tickets, idx, { ...this.tickets[idx], ...detail })
      }

      if (scrollChat) {
        this.scrollToBottomOfChat()
      }
    },
    refreshSelectedTicket(playIncomingSoundIfDriver = false) {
      if (!this.selectedTicket || !this.selectedTicket.id) return
      const prevMaxMsgId = this.lastPolledMessageId
      axios.get(`/operations/support-tickets/${this.selectedTicket.id}`).then(res => {
        if (res && res.data && res.data.data) {
          const detail = res.data.data
          const msgs = detail.messages || []
          if (playIncomingSoundIfDriver && prevMaxMsgId > 0) {
            const newDriverMsgs = msgs.filter(m => m.id > prevMaxMsgId && m.sender_type === 'driver')
            if (newDriverMsgs.length && !detail.is_read_only_for_me) {
              newDriverMsgs.forEach(dm => {
                if (window.__beepgoAuthoritativeSoundGate) {
                  window.__beepgoAuthoritativeSoundGate(['msg_' + dm.id])
                }
              })
            }
          }
          this.applyTicketDetailData(detail, msgs.length > (this.selectedTicket.messages || []).length)
        }
      }).catch(() => {})
    },
    closeWorkspace() {
      this.selectedTicket = null
      this.composerError = null
    },
    scrollToBottomOfChat() {
      this.$nextTick(() => {
        const el = this.$refs.chatScrollBox
        if (el) {
          el.scrollTop = el.scrollHeight
        }
      })
    },
    startLivePolling() {
      this.stopLivePolling()
      this.livePollTimer = window.setInterval(() => {
        if (document.hidden || !this.selectedTicket || this.isPollingActivity) return
        this.isPollingActivity = true
        axios.get(`/operations/support-tickets/${this.selectedTicket.id}/activity`).then(res => {
          if (res && res.data && res.data.data) {
            const act = res.data.data
            const msgChanged = act.latest_message_id > this.lastPolledMessageId
            const histChanged = act.latest_history_id > this.lastPolledHistoryId
            const ownerChanged = act.assigned_to !== this.lastPolledAssignedTo
            const statusChanged = act.status !== this.lastPolledStatus

            if (msgChanged || histChanged || ownerChanged || statusChanged) {
              this.refreshSelectedTicket(msgChanged)
              this.fetchTickets()
            }
          }
        }).catch(() => {}).finally(() => {
          this.isPollingActivity = false
        })
      }, 2000)
    },
    stopLivePolling() {
      if (this.livePollTimer) {
        clearInterval(this.livePollTimer)
        this.livePollTimer = null
      }
    },
    onGlobalOpsActivity(e) {
      const detail = e && e.detail ? e.detail : {}
      const ticketId = detail.ticketId
      if (ticketId) {
        if (!this.selectedTicket || this.selectedTicket.id !== ticketId) {
          if (!this.attentionTicketIds.includes(ticketId)) {
            this.attentionTicketIds.push(ticketId)
          }
        } else {
          this.refreshSelectedTicket(false)
        }
      }
      this.fetchTickets()
    },
    onReplyFilesSelected(e) {
      const files = e.target.files ? Array.from(e.target.files) : []
      this.composer.files = [...this.composer.files, ...files]
      e.target.value = ''
    },
    removeReplyFile(index) {
      this.composer.files.splice(index, 1)
    },
    submitReply() {
      if (!this.selectedTicket || !this.composer.message.trim() || this.submittingReply) return
      this.submittingReply = true
      this.composerError = null

      const formData = new FormData()
      formData.append('message', this.composer.message.trim())
      formData.append('is_internal', this.composer.is_internal ? '1' : '0')
      if (!this.composer.is_internal && this.composer.status) {
        formData.append('status', this.composer.status)
      }
      this.composer.files.forEach(file => {
        formData.append('attachments[]', file)
      })

      axios.post(`/operations/support-tickets/${this.selectedTicket.id}/reply`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then(res => {
        if (res && res.data) {
          this.composer.message = ''
          this.composer.files = []
          if (res.data.data) {
            this.applyTicketDetailData(res.data.data, true)
          }
          this.fetchTickets()
          this.showToast(res.data.message || 'تم حفظ الرد بنجاح', '#047857')
        }
      }).catch(err => {
        const status = err.response ? err.response.status : 500
        const msg = (err.response && err.response.data && err.response.data.message)
          ? err.response.data.message
          : 'حدث خطأ أثناء إرسال الرد'
        this.composerError = msg
        this.showToast(msg, 'error')

        // If 409 Conflict or 403 Forbidden (claimed by another employee), reload ticket immediately in read-only mode
        if (status === 409 || status === 403) {
          this.refreshSelectedTicket(false)
          this.fetchTickets()
        }
      }).finally(() => {
        this.submittingReply = false
      })
    },
    changeTicketStatus(newStatus) {
      if (!this.selectedTicket || !newStatus || newStatus === this.selectedTicket.status) return
      this.updatingStatus = true
      axios.put(`/operations/support-tickets/${this.selectedTicket.id}/status`, {
        status: newStatus,
      }).then(res => {
        if (res && res.data && res.data.data) {
          this.applyTicketDetailData(res.data.data, false)
          this.fetchTickets()
          this.showToast(res.data.message || 'تم تحديث حالة التذكرة', '#082C69')
        }
      }).catch(err => {
        const msg = (err.response && err.response.data && err.response.data.message)
          ? err.response.data.message
          : 'تعذر تحديث الحالة'
        this.showToast(msg, 'error')
        this.refreshSelectedTicket(false)
      }).finally(() => {
        this.updatingStatus = false
      })
    },
    changeTicketPriority(newPriority) {
      if (!this.selectedTicket || !newPriority || newPriority === this.selectedTicket.priority) return
      this.updatingPriority = true
      axios.put(`/operations/support-tickets/${this.selectedTicket.id}/priority`, {
        priority: newPriority,
      }).then(res => {
        if (res && res.data && res.data.data) {
          this.applyTicketDetailData(res.data.data, false)
          this.fetchTickets()
          this.showToast(res.data.message || 'تم تحديث الأولوية', '#082C69')
        }
      }).catch(err => {
        const msg = (err.response && err.response.data && err.response.data.message)
          ? err.response.data.message
          : 'تعذر تحديث الأولوية'
        this.showToast(msg, 'error')
      }).finally(() => {
        this.updatingPriority = false
      })
    },
    openReassignDialog() {
      if (!this.selectedTicket) return
      this.reassignDialog.assigned_to = this.selectedTicket.assigned_to || null
      this.reassignDialog.reason = ''
      this.reassignDialog.open = true
    },
    confirmReassign() {
      if (!this.selectedTicket || !this.reassignDialog.assigned_to || !this.reassignDialog.reason.trim()) return
      this.reassignDialog.loading = true
      axios.post(`/operations/support-tickets/${this.selectedTicket.id}/reassign`, {
        assigned_to: this.reassignDialog.assigned_to,
        reason: this.reassignDialog.reason.trim(),
      }).then(res => {
        this.reassignDialog.open = false
        if (res && res.data && res.data.data) {
          this.applyTicketDetailData(res.data.data, false)
          this.fetchTickets()
          this.showToast(res.data.message || 'تمت إعادة توجيه التذكرة بنجاح', '#047857')
        }
      }).catch(err => {
        const msg = (err.response && err.response.data && err.response.data.message)
          ? err.response.data.message
          : 'تعذر إعادة توجيه التذكرة'
        this.showToast(msg, 'error')
      }).finally(() => {
        this.reassignDialog.loading = false
      })
    },
    openReleaseDialog() {
      if (!this.selectedTicket) return
      this.releaseDialog.reason = ''
      this.releaseDialog.open = true
    },
    confirmRelease() {
      if (!this.selectedTicket || !this.releaseDialog.reason.trim()) return
      this.releaseDialog.loading = true
      axios.post(`/operations/support-tickets/${this.selectedTicket.id}/release`, {
        reason: this.releaseDialog.reason.trim(),
      }).then(res => {
        this.releaseDialog.open = false
        if (res && res.data && res.data.data) {
          this.applyTicketDetailData(res.data.data, false)
          this.fetchTickets()
          this.showToast(res.data.message || 'تم إلغاء تخصيص التذكرة', '#082C69')
        }
      }).catch(err => {
        const msg = (err.response && err.response.data && err.response.data.message)
          ? err.response.data.message
          : 'تعذر إلغاء التخصيص'
        this.showToast(msg, 'error')
      }).finally(() => {
        this.releaseDialog.loading = false
      })
    },
    downloadAttachment(att) {
      if (!att || !att.id || !this.selectedTicket) return
      axios.get(`/operations/support-tickets/${this.selectedTicket.id}/attachments/${att.id}/download`, {
        responseType: 'blob',
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', att.file_name || 'attachment')
        document.body.appendChild(link)
        link.click()
        link.remove()
      }).catch(() => {
        this.showToast('تعذر تحميل المرفق', 'error')
      })
    },
    formatDateTime(val) {
      if (!val) return '—'
      return String(val).replace('T', ' ').substring(0, 16)
    },
    formatHistoryActionLabel(actionType) {
      const map = {
        ticket_created: 'إنشاء التذكرة',
        ticket_claimed: 'استلام التذكرة (أول مستجيب)',
        operations_reply: 'رد فريق التشغيل',
        driver_reply: 'رد من السائق',
        internal_note_added: 'ملاحظة داخلية',
        status_changed: 'تغيير الحالة',
        priority_changed: 'تغيير الأولوية',
        ticket_reassigned: 'إعادة توجيه الملكية',
        ticket_released: 'إلغاء تخصيص الملكية',
        ticket_closed: 'إغلاق التذكرة',
        ticket_reopened: 'إعادة فتح التذكرة',
      }
      return map[actionType] || actionType
    },
  },
}
</script>

<style scoped>
.ops-support-page {
  font-family: 'Cairo', sans-serif !important;
}
.navy--text {
  color: #082C69 !important;
}
.text-dark {
  color: #0F172A !important;
}
.text-secondary {
  color: #475569 !important;
}
.amber-dark--text {
  color: #92400E !important;
}
.green-dark--text {
  color: #065F46 !important;
}
.slate--text {
  color: #334155 !important;
}
.page-header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background-color: #EFF6FF;
  border: 1px solid #BFDBFE;
  display: flex;
  align-items: center;
  justify-content: center;
}
.kpi-card {
  border: 1px solid #CBD5E1 !important;
  background: #FFFFFF;
  transition: all 0.15s ease;
}
.kpi-card:hover {
  border-color: #082C69 !important;
  transform: translateY(-1px);
}
.kpi-active {
  border: 2px solid #082C69 !important;
  background-color: #F0F7FF !important;
}
.kpi-active-amber {
  border: 2px solid #D97706 !important;
  background-color: #FFFBEB !important;
}
.kpi-active-green {
  border: 2px solid #059669 !important;
  background-color: #ECFDF5 !important;
}
.kpi-active-red {
  border: 2px solid #DC2626 !important;
  background-color: #FEF2F2 !important;
}
.filter-card,
.table-card,
.workspace-card {
  border: 1px solid #CBD5E1 !important;
}
.bg-light-header {
  background-color: #F8FAFC;
}
.border-bottom {
  border-bottom: 1px solid #E2E8F0;
}
.border-top {
  border-top: 1px solid #E2E8F0;
}
.border-bottom-dashed {
  border-bottom: 1px dashed #E2E8F0;
}
.ops-tickets-table th {
  background-color: #F8FAFC !important;
  color: #082C69 !important;
  font-weight: 700 !important;
  font-size: 12px !important;
}
.ticket-row:hover {
  background-color: #F8FAFC !important;
}
.selected-ticket-row {
  background-color: #EFF6FF !important;
}
.attention-ticket-row {
  background-color: #FFFBEB !important;
}
.mono-badge {
  font-family: monospace;
  font-size: 12px;
  background-color: #E0E7FF;
  color: #082C69;
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid #C7D2FE;
}
.mono-badge-lg {
  font-family: monospace;
  font-size: 13px;
  background-color: #082C69;
  color: #FFFFFF;
  padding: 3px 10px;
  border-radius: 8px;
}
.mono-code {
  font-family: monospace;
}
.type-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 5px;
}
.type-complaint {
  background-color: #E0E7FF;
  color: #1E40AF;
}
.type-report {
  background-color: #FEF3C7;
  color: #92400E;
}
.priority-pill,
.status-pill,
.owner-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
}
.prio-low {
  background-color: #F1F5F9;
  color: #475569;
}
.prio-normal,
.prio-medium {
  background-color: #E0F2FE;
  color: #0369A1;
}
.prio-high {
  background-color: #FFEDD5;
  color: #C2410C;
}
.prio-urgent {
  background-color: #FEE2E2;
  color: #B91C1C;
  border: 1px solid #FECACA;
}
.st-new {
  background-color: #DBEAFE;
  color: #1D4ED8;
}
.st-in_review {
  background-color: #E0F2FE;
  color: #0369A1;
}
.st-waiting_driver {
  background-color: #FEF3C7;
  color: #B45309;
}
.st-replied,
.st-resolved {
  background-color: #D1FAE5;
  color: #047857;
}
.st-closed {
  background-color: #E2E8F0;
  color: #334155;
}
.owner-unassigned {
  background-color: #FEF3C7;
  color: #B45309;
  border: 1px solid #FDE68A;
}
.owner-mine {
  background-color: #D1FAE5;
  color: #065F46;
  border: 1px solid #A7F3D0;
}
.owner-other {
  background-color: #F1F5F9;
  color: #334155;
  border: 1px solid #CBD5E1;
}
.readonly-mini-badge {
  font-size: 10px;
  font-weight: 700;
  background-color: #FEE2E2;
  color: #991B1B;
  padding: 1px 6px;
  border-radius: 4px;
}
.workspace-header {
  background-color: #F8FAFC;
  border-bottom: 1px solid #CBD5E1;
}
.ownership-banner {
  border-bottom: 1px solid #E2E8F0;
}
.banner-readonly {
  background-color: #FEF2F2;
  border-bottom-color: #FECACA;
}
.banner-unassigned {
  background-color: #FFFBEB;
  border-bottom-color: #FDE68A;
}
.banner-owned {
  background-color: #ECFDF5;
  border-bottom-color: #A7F3D0;
}
.readonly-badge {
  background-color: #DC2626;
  color: #FFFFFF;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
}
.unassigned-badge {
  background-color: #D97706;
  color: #FFFFFF;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
}
.owned-badge {
  background-color: #059669;
  color: #FFFFFF;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
}
.bg-ticket-summary {
  background-color: #F8FAFC;
}
.chat-thread-container {
  max-height: 440px;
  min-height: 260px;
  overflow-y: auto;
  background-color: #F1F5F9;
}
.chat-bubble {
  max-width: 85%;
  border: 1px solid #E2E8F0;
}
.wrapper-driver {
  display: flex;
  justify-content: flex-start;
}
.wrapper-staff,
.wrapper-internal {
  display: flex;
  justify-content: flex-end;
}
.bubble-driver {
  background-color: #FFFFFF;
  border-color: #CBD5E1;
}
.bubble-staff {
  background-color: #EFF6FF;
  border-color: #BFDBFE;
}
.bubble-internal {
  background-color: #FFFBEB;
  border-color: #FCD34D;
}
.role-mini-tag {
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
}
.tag-driver {
  background-color: #E0E7FF;
  color: #082C69;
}
.tag-ops {
  background-color: #D1FAE5;
  color: #065F46;
}
.tag-internal {
  background-color: #FEF3C7;
  color: #92400E;
}
.bg-side-panel {
  background-color: #F8FAFC;
}
.bg-blue-soft {
  background-color: #EFF6FF;
  border: 1px solid #BFDBFE;
}
.bg-readonly-soft {
  background-color: #FEF2F2;
  border: 1px solid #FECACA;
}
.history-timeline-list {
  max-height: 290px;
  overflow-y: auto;
}
.whitespace-pre-wrap {
  white-space: pre-wrap;
}
.live-pulse-dot {
  width: 8px;
  height: 8px;
  background-color: #10B981;
  border-radius: 50%;
  display: inline-block;
}
.attention-dot {
  width: 9px;
  height: 9px;
  background-color: #EF4444;
  border-radius: 50%;
  display: inline-block;
}
</style>
